import {
    PUBLISHED_BLOCKS,
    getBlockPath,
    BLOCKS_PATH
} from "./blocks";

export type DocsNavItem = {
    isDeprecated?: boolean;
    exampleRoute?: string;
    titleKey?: string;
    groupKey: string;
    leadKey: string;
    isNew?: boolean;
    title?: string;
    index: number;
    path: string;
};

export type DocsNavGroup = {
    items: Array<DocsNavItem>;
    titleKey: string;
    id: string;
};

export type DocsNavSection = {
    groups: Array<DocsNavGroup>;
    isCollapsible: boolean;
    titleKey: string;
    id: string;
};

const NEW_COMPONENTS = new Set([
    "ColorSwatchPicker",
    "ColorPicker",
    "ColorSwatch",
    "ColorSlider",
    "ColorField",
    "ColorArea",
    "Badge",
    "Label",
    "RadioGroup",
    "ScrollArea",
    "Empty",
    "Field",
    "Item",
    "MessageScroller",
    "DirectionProvider",
    "NavigationMenu",
    "SwipeableRow",
    "AnimatedIcon",
    "DropdownMenu",
    "BrandIcons",
    "Questionnaire",
    "ContextMenu",
    "AspectRatio",
    "ToggleGroup",
    "ButtonGroup",
    "NativeSelect",
    "Collapsible",
    "FileUpload",
    "InputGroup",
    "Pagination",
    "Breadcrumb",
    "PieChart",
    "RadarChart",
    "RadialChart",
    "Sparkline",
    "Attachment",
    "Accordion",
    "Resizable",
    "HoverCard",
    "DataTable",
    "AreaChart",
    "LineChart",
    "Timeline",
    "TreeView",
    "Carousel",
    "Combobox",
    "InputOTP",
    "Skeleton",
    "Progress",
    "BarChart",
    "StatTile",
    "Composer",
    "Menubar",
    "Popover",
    "Sidebar",
    "Command",
    "Stepper",
    "Message",
    "Slider",
    "Marker",
    "Citation",
    "Toggle",
    "TabBar",
    "Bubble",
    "Trace",
    "Table",
    "Sheet",
    "Chart",
    "Alert",
    "Card",
    "Tabs",
    "Icon",
    "Flag",
    "Kbd"
]);

const DEPRECATED_COMPONENTS = new Set([
    "NotificationIndicator",
    "StateCard",
    "SnackBar",
    "Sticker",
    "RowCard"
]);

const COMPONENT_GROUPS: Array<{
    names: Array<string>;
    titleKey: string;
    id: string;
}> = [
    {
        names: [
            "Text",
            "Icon",
            "BrandIcons",
            "AnimatedIcon",
            "Kbd",
            "Flag",
            "Avatar",
            "AvatarGroup",
            "Chip",
            "Badge",
            "Sticker",
            "Separator",
            "Marker",
            "Skeleton",
            "Progress",
            "Loading",
            "NotificationIndicator",
            "SiteLogo",
            "Card",
            "Item",
            "Alert",
            "Collapsible",
            "Accordion",
            "AspectRatio",
            "Resizable",
            "CodeViewer",
            "MarkdownViewer",
            "DirectionProvider"
        ],
        titleKey: "group.basic",
        id: "basic"
    },
    {
        titleKey: "group.actions",
        names: [
            "Button",
            "HighlightButton",
            "ButtonGroup",
            "Toggle",
            "ToggleGroup",
            "ThemeSwitcher",
            "PaletteSwitcher",
            "LocaleSwitcher"
        ],
        id: "actions"
    },
    {
        names: [
            "TextInput",
            "TextAreaInput",
            "NumericInput",
            "InputOTP",
            "InputGroup",
            "SelectBox",
            "Combobox",
            "CheckBox",
            "RadioButton",
            "RadioGroup",
            "Switch",
            "Label",
            "Field",
            "Slider",
            "ColorField",
            "ColorPicker",
            "ColorArea",
            "ColorSlider",
            "ColorSwatch",
            "ColorSwatchPicker",
            "DateTimePicker",
            "DateSelector",
            "TimeSelector",
            "MonthSelector",
            "YearSelector",
            "FileUpload",
            "Questionnaire",
            "CodeEditor",
            "MarkdownEditor"
        ],
        titleKey: "group.form",
        id: "form"
    },
    {
        titleKey: "group.layers",
        names: [
            "Dialog",
            "Modal",
            "Sheet",
            "Popover",
            "Tooltip",
            "HoverCard",
            "DropdownMenu",
            "ContextMenu",
            "Command",
            "SnackBar",
            "Toast"
        ],
        id: "layers"
    },
    {
        titleKey: "group.navigation",
        names: [
            "Menu",
            "EmbeddedMenu",
            "Menubar",
            "NavigationMenu",
            "Sidebar",
            "Tabs",
            "Breadcrumb",
            "Pagination",
            "Stepper",
            "Header",
            "MainHeader",
            "PageContainer",
            "SystemToolbar",
            "UserShortcut",
            "WebScrollbar",
            "ScrollArea"
        ],
        id: "navigation"
    },
    {
        titleKey: "group.data",
        names: [
            "Table",
            "DataTable",
            "Chart",
            "BarChart",
            "LineChart",
            "AreaChart",
            "PieChart",
            "RadarChart",
            "RadialChart",
            "Sparkline",
            "StatTile",
            "Timeline",
            "TreeView",
            "RowCard",
            "Empty",
            "StateCard",
            "Carousel"
        ],
        id: "data"
    },
    {
        titleKey: "group.chat",
        names: [
            "Message",
            "MessageScroller",
            "Bubble",
            "Composer",
            "Trace",
            "Citation",
            "Attachment"
        ],
        id: "chat"
    },
    {
        titleKey: "group.mobile",
        names: [
            "BottomSheet",
            "SelectSheet",
            "DateTimeSheet",
            "NativeSelect",
            "TabBar",
            "SwipeableRow"
        ],
        id: "mobile"
    }
];

export const toKebabCase = (name: string) => name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").replace(/([A-Z])([A-Z][a-z])/g, "$1-$2").toLowerCase();

export const getComponentPath = (name: string) => `/components/${toKebabCase(name)}`;

const GUIDE_ITEMS: Array<{
    titleKey: string;
    leadKey: string;
    path: string;
}> = [
    {
        titleKey: "page.introduction",
        leadKey: "intro.lead",
        path: "/"
    },
    {
        titleKey: "page.installation",
        leadKey: "install.lead",
        path: "/installation"
    },
    {
        titleKey: "page.theming",
        leadKey: "theming.lead",
        path: "/theming"
    },
    {
        titleKey: "page.direction",
        leadKey: "direction.lead",
        path: "/direction"
    },
    {
        titleKey: "page.ai",
        leadKey: "ai.lead",
        path: "/ai"
    }
];

export const RELEASE_NOTES_PATHS = {
    deprecated: "/release-notes/deprecated",
    whatsNew: "/release-notes/v2"
};

const RELEASE_NOTE_ITEMS: Array<{
    titleKey: string;
    leadKey: string;
    path: string;
}> = [
    {
        leadKey: "releaseNotes.whatsNewLead",
        path: RELEASE_NOTES_PATHS.whatsNew,
        titleKey: "page.whatsNew"
    },
    {
        leadKey: "releaseNotes.deprecatedLead",
        path: RELEASE_NOTES_PATHS.deprecated,
        titleKey: "page.deprecated"
    }
];

const FOUNDATION_ITEMS: Array<{
    titleKey: string;
    leadKey: string;
    path: string;
}> = [
    {
        leadKey: "foundation.colorLead",
        path: "/foundations/color",
        titleKey: "page.color"
    },
    {
        leadKey: "foundation.typographyLead",
        path: "/foundations/typography",
        titleKey: "page.typography"
    },
    {
        leadKey: "foundation.spacingLead",
        path: "/foundations/spacing",
        titleKey: "page.spacing"
    },
    {
        leadKey: "foundation.motionLead",
        path: "/foundations/motion",
        titleKey: "page.motion"
    }
];

let componentIndex = 0;

export const DOCS_NAV: Array<DocsNavSection> = [
    {
        groups: [
            {
                items: GUIDE_ITEMS.map((item, index) => ({
                    ...item,
                    groupKey: "nav.sectionGettingStarted",
                    index: index + 1
                })),
                titleKey: "nav.sectionGettingStarted",
                id: "getting-started"
            }
        ],
        titleKey: "nav.sectionGettingStarted",
        id: "getting-started",
        isCollapsible: false
    },
    {
        groups: [
            {
                items: RELEASE_NOTE_ITEMS.map((item, index) => ({
                    ...item,
                    groupKey: "nav.sectionReleaseNotes",
                    index: index + 1
                })),
                titleKey: "nav.sectionReleaseNotes",
                id: "release-notes"
            }
        ],
        titleKey: "nav.sectionReleaseNotes",
        id: "release-notes",
        isCollapsible: false
    },
    {
        groups: [
            {
                items: FOUNDATION_ITEMS.map((item, index) => ({
                    ...item,
                    groupKey: "nav.sectionFoundations",
                    index: index + 1
                })),
                titleKey: "nav.sectionFoundations",
                id: "foundations"
            }
        ],
        titleKey: "nav.sectionFoundations",
        isCollapsible: false,
        id: "foundations"
    },
    {
        groups: COMPONENT_GROUPS.map((group) => ({
            items: group.names.map((name) => {
                componentIndex += 1;

                return {
                    leadKey: `${name.charAt(0).toLowerCase()}${name.slice(1)}Page.lead`,
                    isDeprecated: DEPRECATED_COMPONENTS.has(name),
                    exampleRoute: name.toLowerCase(),
                    isNew: NEW_COMPONENTS.has(name),
                    path: getComponentPath(name),
                    groupKey: group.titleKey,
                    index: componentIndex,
                    title: name
                };
            }),
            titleKey: group.titleKey,
            id: group.id
        })),
        titleKey: "nav.sectionComponents",
        isCollapsible: true,
        id: "components"
    }
];

export const BLOCKS_NAV: DocsNavSection = {
    groups: [
        {
            items: PUBLISHED_BLOCKS.map((block, index) => ({
                groupKey: "nav.sectionBlocks",
                path: getBlockPath(block.id),
                titleKey: block.titleKey,
                leadKey: block.leadKey,
                index: index + 1
            })),
            titleKey: "nav.sectionBlocks",
            id: "blocks"
        }
    ],
    titleKey: "nav.sectionBlocks",
    isCollapsible: false,
    id: "blocks"
};

export const CREATE_PATH = "/create";

export const CREATE_NAV: DocsNavSection = {
    groups: [
        {
            items: [
                {
                    titleKey: "create.title",
                    groupKey: "create.nav",
                    leadKey: "create.lead",
                    path: CREATE_PATH,
                    index: 1
                }
            ],
            titleKey: "create.nav",
            id: "create"
        }
    ],
    titleKey: "create.nav",
    isCollapsible: false,
    id: "create"
};

export const DOCS_PAGES: Array<DocsNavItem> = DOCS_NAV.flatMap((section) => section.groups.flatMap((group) => group.items));

const BLOCK_NAV_PAGES: Array<DocsNavItem> = [
    {
        groupKey: "nav.sectionBlocks",
        titleKey: "nav.sectionBlocks",
        leadKey: "blocks.indexLead",
        path: BLOCKS_PATH,
        index: 0
    },
    ...BLOCKS_NAV.groups.flatMap((group) => group.items)
];

export const COMPONENT_COUNT = (DOCS_NAV.find((section) => section.id === "components")?.groups ?? []).reduce((total, group) => total + group.items.length, 0);

export const COMPONENT_GROUP_COUNT = COMPONENT_GROUPS.length;

const CREATE_NAV_PAGES: Array<DocsNavItem> = CREATE_NAV.groups.flatMap((group) => group.items);

export const findPage = (path: string) => DOCS_PAGES.find((page) => page.path === path) ?? BLOCK_NAV_PAGES.find((page) => page.path === path) ?? CREATE_NAV_PAGES.find((page) => page.path === path);

export const findGroupOfPath = (path: string) => DOCS_NAV.flatMap((section) => section.groups).find((group) => group.items.some((item) => item.path === path));
